import React, { useState, useCallback, useEffect } from "react";
import { PreSubmissionConfigurationModel } from "../../../models/BatchMessage/BatchMessageDetailModel";
import "../IVRFlowConfiguration/IVRFlowConfiguration.css";
import { GetAllAccessibleCountries } from "../../../api/countryController";
import { IVRFlowTypeEnum } from "../../../common/enum";
import {
	Grid,
	Autocomplete,
	TextField,
} from "../../../common/MaterialComponent";
import Shared from "../../../common/shared";
import CountryModel from "../../../models/Country/CountryModel";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export interface IVRFlowTypeConfigurationProp {
	flowConfigurationDetail: PreSubmissionConfigurationModel;
	highlightRequiredDetail: boolean;
	onFlowConfigurationDetailChange: React.Dispatch<
		React.SetStateAction<PreSubmissionConfigurationModel>
	>;
	setHasFlowConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

export const IVRFlowTypeConfiguration: React.FC<IVRFlowTypeConfigurationProp> = ({
	flowConfigurationDetail,
	highlightRequiredDetail,
	onFlowConfigurationDetailChange,
	setHasFlowConfigChanged,
}) => {
	const [countryList, setCountryList] = useState<CountryModel[]>([]);
	const [selectedCountry, setSelectedCountry] = useState<CountryModel | null>(
		null
	);
	const [flowTypeList, setFlowTypeList] = useState<string[]>([]);
	const strings = useGetComponentStringsContext().IVRFlowTypeConfiguration;

	useEffect(() => {
		const arrayObjects: any = [];
		for (const [propertyKey] of Object.entries(IVRFlowTypeEnum)) {
			if (!Number.isNaN(Number(propertyKey))) {
				continue;
			}
			arrayObjects.push(propertyKey);
		}
		setFlowTypeList(arrayObjects);
	}, []);

	const setSelectedCountryDetail = useCallback(
		(countryList: CountryModel[]) => {
			// set country as selected from country list if already saved
			const countryDetail = countryList.find(
				(c) => c.id === flowConfigurationDetail?.country
			);
			if (countryDetail) {
				setSelectedCountry(countryDetail);
			}
			if (
				!flowConfigurationDetail.id &&
				!flowConfigurationDetail.country &&
				countryList.length === 1
			) {
				onFlowConfigurationDetailChange((prevBatchMessageDetail) => {
					return {
						...prevBatchMessageDetail,
						// if only one country available for user then by default make it selected
						country: countryList[0].id as string,
					};
				});
				setSelectedCountry(countryList[0]);
				setHasFlowConfigChanged(true);
			}
		},
		[
			flowConfigurationDetail.country,
			flowConfigurationDetail.id,
			onFlowConfigurationDetailChange,
			setHasFlowConfigChanged,
		]
	);

	useEffect(() => {
		let isMounted = true;
		async function getCountryList() {
			//only run if country list empty
			if (countryList.length <= 0) {
				try {
					const res = await GetAllAccessibleCountries();
					if (res.error) {
						Shared.showSnackbarMessage(strings.countryListErrorMessage);
					} else {
						if (isMounted) {
							setCountryList(res.responseObj ? res.responseObj : []);
							setSelectedCountryDetail(res.responseObj as CountryModel[]);
						}
					}
				} catch (err) {
					Shared.showSnackbarMessage(strings.countryListErrorMessage);
				}
			}
		}
		getCountryList();
		return () => {
			isMounted = false;
		};
	}, [
		countryList.length,
		setSelectedCountryDetail,
		strings.countryListErrorMessage,
	]);

	const handleCountryChange = (event: any, value: CountryModel | null) => {
		setSelectedCountry(value);
		onFlowConfigurationDetailChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				country: value?.id as string,
			};
		});
		setHasFlowConfigChanged(true);
	};

	const handleDescriptionChange = (event: any) => {
		onFlowConfigurationDetailChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				title: event.target.value as string,
			};
		});
		setHasFlowConfigChanged(true);
	};

	const handleFlowTypeChange = (event: any, value: any) => {
		if (flowConfigurationDetail.id) {
			return;
		}
		onFlowConfigurationDetailChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				ivrFlowType: value!,
			};
		});
		setHasFlowConfigChanged(true);
	};

	const shouldFocus = (callingElement: "Country" | "Description" | "Type") => {
		//only focus if highlight detail is true
		if (highlightRequiredDetail === false) {
			return false;
		}
		if (
			flowConfigurationDetail.country === null ||
			flowConfigurationDetail.title === null ||
			flowConfigurationDetail.ivrFlowType === null
		) {
			//error
			return false;
		}
		//returns true if should focus element
		if (flowConfigurationDetail.country?.length <= 0) {
			//returns true if country name calling function
			if (callingElement === "Country") {
				setHasFlowConfigChanged(true);
				return true;
			}
		} else if (flowConfigurationDetail.title.length <= 0) {
			//returns true and should focus if calling component was description
			if (callingElement === "Description") {
				setHasFlowConfigChanged(true);
				return true;
			}
		} else if (flowConfigurationDetail.ivrFlowType.length <= 0) {
			//returns true and should focus if calling component was type
			if (callingElement === "Type") {
				setHasFlowConfigChanged(true);
				return true;
			}
		} else {
			return false;
		}
		return false;
	};

	return (
		<>
			<div data-testid="ivrConfiguration">
				<Grid item xs={6} className="gridSpace">
					<Autocomplete
						data-testid="countryList"
						options={countryList}
						onChange={handleCountryChange}
						autoHighlight
						value={selectedCountry}
						getOptionLabel={(option) => option.id}
						getOptionSelected={(option, value) => !value || option === value}
						renderInput={(params) => (
							<TextField
								{...params}
								required
								label={strings.countryLabel}
								helperText={
									highlightRequiredDetail && !flowConfigurationDetail.country
										? strings.countryErrorMessage
										: ""
								}
								error={
									highlightRequiredDetail && !flowConfigurationDetail.country
								}
								inputRef={(input) =>
									input && shouldFocus("Country") && input.focus()
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={6} className="gridSpace">
					<TextField
						data-testid="descriptionInput"
						fullWidth
						required
						label={strings.descriptionLabel}
						value={flowConfigurationDetail.title}
						onChange={handleDescriptionChange}
						inputRef={(input) =>
							input && shouldFocus("Description") && input.focus()
						}
						error={highlightRequiredDetail && !flowConfigurationDetail.title}
						helperText={
							highlightRequiredDetail && !flowConfigurationDetail.title
								? strings.descriptionErrorMessage
								: ""
						}
					></TextField>
				</Grid>
				<Grid item xs={6} className="gridSpace">
					<Autocomplete
						data-testid="type"
						options={flowTypeList}
						onChange={handleFlowTypeChange}
						autoHighlight
						value={flowConfigurationDetail.ivrFlowType}
						getOptionLabel={(option) => option}
						disableClearable={flowConfigurationDetail.id ? true : false}
						getOptionSelected={(option, value) => !value || option === value}
						disabled={flowConfigurationDetail.id ? true : false}
						renderInput={(params) => (
							<TextField
								{...params}
								required
								disabled={flowConfigurationDetail.id ? true : false}
								label={strings.typeLabel}
								inputRef={(input) =>
									input && shouldFocus("Type") && input.focus()
								}
								helperText={
									highlightRequiredDetail &&
									!flowConfigurationDetail.ivrFlowType
										? strings.typeErrorMessage
										: ""
								}
								error={
									highlightRequiredDetail &&
									!flowConfigurationDetail.ivrFlowType
								}
							/>
						)}
					/>
				</Grid>
			</div>
		</>
	);
};
